/**
 * useMediaSession — W3C Media Session API for lock screen / Control Center controls.
 *
 * Works in Safari (iPad/iPhone), Chrome, Firefox, Edge — any browser that
 * supports navigator.mediaSession. Gives the OS track metadata (title, artist,
 * album, artwork) and registers play/pause/skip/seek handlers so the lock
 * screen transport controls work.
 *
 * This hook is the BROWSER path. The native iOS app uses useIOSNowPlaying
 * instead (via window.__maestroNative). Both hooks self-guard so only one
 * is active at a time.
 *
 * macOS media keys (F7/F8/F9) route through this API — they never arrive
 * as keydown events. Chrome routes F8 (play/pause) to either the 'play'
 * or 'pause' handler based on navigator.mediaSession.playbackState.
 * If playbackState is 'none', Chrome doesn't know which handler to call
 * and the key falls through to Apple Music. To prevent this, we use
 * 'paused' (not 'none') whenever a track is loaded but not playing.
 *
 * Session activation: Chrome only claims the macOS Now Playing session
 * when an actual <audio>/<video> element is playing. AudioContext alone
 * does NOT work. We play a hidden silent <audio> loop to claim the
 * session even when music routes to a network renderer (UPnP/RAAT).
 * The silent element is created on first user gesture (play action),
 * kept alive during paused state (to retain session ownership), and
 * only suspended when no track is loaded.
 *
 * NOTE: If Apple Music is already running and owns the Now Playing
 * session, it may intercept media keys before Chrome can. The user
 * needs to quit Music.app (or install noTunes) for media keys to
 * route to the browser. This is a macOS limitation.
 *
 * Position and duration are read directly from the HTML <audio> element
 * (which is in seconds) — NOT from the Zustand store, which may lag or
 * use different units depending on the transport path.
 */
import { useEffect, useRef, useCallback } from 'react';
import { useMaestroStore } from '../store/maestroStore';
import { isIOSNativeApp } from '../utils/platform';

const POSITION_POLL_MS = 5_000;

// 0.1-second silent WAV (800 samples @ 8kHz, 16-bit mono PCM, all zeros).
// Loops continuously to keep Chrome's media session active.
const SILENT_WAV = 'data:audio/wav;base64,UklGRmQGAABXQVZFZm10IBAAAAABAAEAQB8AAIA+AAACABAAZGF0YUAGAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA';

function isActive(): boolean {
  if (isIOSNativeApp()) return false;
  return (
    typeof navigator !== 'undefined' &&
    'mediaSession' in navigator
  );
}

function getMusicAudioElement(): HTMLAudioElement | null {
  return document.querySelector('audio[data-maestro-music="true"]');
}

export function useMediaSession(): void {
  const currentTrack = useMaestroStore((s) => s.currentTrack);
  const playbackState = useMaestroStore((s) => s.playbackState);
  const queueIndex = useMaestroStore((s) => s.queueIndex);
  const positionTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const silentAudioRef = useRef<HTMLAudioElement | null>(null);

  const clearTimer = useCallback(() => {
    if (positionTimerRef.current) {
      clearInterval(positionTimerRef.current);
      positionTimerRef.current = null;
    }
  }, []);

  // ── Silent <audio> activator — claims the macOS Now Playing session ──
  // Chrome only registers a media session when an <audio>/<video> element
  // is playing. When Maestro sends music to a network renderer, no browser
  // audio is produced, so macOS routes F7/F8/F9 to Apple Music.
  // This hidden element plays a looping silent WAV at near-zero volume
  // to make Chrome claim the session. It stays playing during pause to
  // retain session ownership. Only suspended when nothing is loaded.
  useEffect(() => {
    if (!isActive()) return;

    const hasTrack = !!currentTrack;
    const isPlaying = playbackState === 'playing';

    if (hasTrack && (isPlaying || playbackState === 'paused' || playbackState === 'stopped')) {
      if (!silentAudioRef.current) {
        const el = document.createElement('audio');
        el.src = SILENT_WAV;
        el.loop = true;
        el.volume = 0.001;
        el.setAttribute('data-maestro-session', 'true');
        silentAudioRef.current = el;
      }

      if (silentAudioRef.current.paused) {
        silentAudioRef.current.play().catch(() => {});
      }
    } else if (!hasTrack && silentAudioRef.current && !silentAudioRef.current.paused) {
      silentAudioRef.current.pause();
    }
  }, [playbackState, currentTrack]);

  // Cleanup on unmount — pause and release the element
  useEffect(() => {
    return () => {
      if (silentAudioRef.current) {
        silentAudioRef.current.pause();
        silentAudioRef.current.src = '';
        silentAudioRef.current = null;
      }
    };
  }, []);

  // ── Track metadata + artwork ────────────────────────────────────
  useEffect(() => {
    if (!isActive()) return;

    if (!currentTrack) {
      navigator.mediaSession.metadata = null;
      return;
    }

    const artSrc =
      currentTrack.artworkUrl ||
      currentTrack.artworkUrlHiRes ||
      (currentTrack as any).coverUrl ||
      '';

    const artwork: MediaImage[] = artSrc
      ? [
          { src: artSrc, sizes: '96x96', type: 'image/jpeg' },
          { src: artSrc, sizes: '256x256', type: 'image/jpeg' },
          { src: artSrc, sizes: '512x512', type: 'image/jpeg' },
        ]
      : [];

    navigator.mediaSession.metadata = new MediaMetadata({
      title: currentTrack.title || '',
      artist: currentTrack.artist || '',
      album: currentTrack.album || '',
      artwork,
    });
  }, [currentTrack, queueIndex]);

  // ── Playback state ──────────────────────────────────────────────
  // Chrome uses this to decide whether F8 triggers the 'play' or 'pause'
  // handler. If 'none', Chrome doesn't route F8 at all and macOS sends
  // it to Apple Music. So we use 'paused' whenever a track is loaded.
  useEffect(() => {
    if (!isActive()) return;

    if (playbackState === 'playing') {
      navigator.mediaSession.playbackState = 'playing';
    } else if (currentTrack) {
      navigator.mediaSession.playbackState = 'paused';
    } else {
      navigator.mediaSession.playbackState = 'none';
    }
  }, [playbackState, currentTrack]);

  // ── Position state (lock screen progress bar) ───────────────────
  // Reads directly from the music <audio> element — store position/duration
  // are NOT used here because they can lag or use different units.
  useEffect(() => {
    if (!isActive()) return;

    clearTimer();

    const pushPosition = () => {
      const audio = getMusicAudioElement();
      if (!audio) return;

      const dur = Number.isFinite(audio.duration) ? audio.duration : 0;
      const pos = Number.isFinite(audio.currentTime) ? audio.currentTime : 0;
      if (dur <= 0) return;

      try {
        navigator.mediaSession.setPositionState({
          duration: dur,
          position: Math.min(Math.max(0, pos), dur),
          playbackRate: 1,
        });
      } catch {
        // Safari may reject during source transitions
      }
    };

    pushPosition();

    if (playbackState === 'playing') {
      positionTimerRef.current = setInterval(pushPosition, POSITION_POLL_MS);
    }

    return clearTimer;
  }, [playbackState, clearTimer]);

  // ── Action handlers (lock screen + media key transport controls) ─
  // Registered BEFORE the silent audio starts playing — order matters
  // on Chrome for media session association.
  useEffect(() => {
    if (!isActive()) return;

    const actions: [MediaSessionAction, MediaSessionActionHandler][] = [
      [
        'play',
        async () => {
          const { TransportCommand } = await import('../transport/TransportCommand');
          TransportCommand.play();
        },
      ],
      [
        'pause',
        async () => {
          const { TransportCommand } = await import('../transport/TransportCommand');
          TransportCommand.pause(true);
        },
      ],
      [
        'previoustrack',
        async () => {
          const { QueueController } = await import('../transport/QueueController');
          QueueController.previous();
        },
      ],
      [
        'nexttrack',
        async () => {
          const { QueueController } = await import('../transport/QueueController');
          QueueController.next();
        },
      ],
      [
        'seekto',
        async (details) => {
          if (details.seekTime == null) return;
          const { TransportCommand } = await import('../transport/TransportCommand');
          TransportCommand.seek(details.seekTime);
        },
      ],
    ];

    for (const [action, handler] of actions) {
      try {
        navigator.mediaSession.setActionHandler(action, handler);
      } catch {
        // Browser may not support this action
      }
    }

    return () => {
      for (const [action] of actions) {
        try {
          navigator.mediaSession.setActionHandler(action, null);
        } catch {}
      }
    };
  }, []);
}
